import {
  formatDateToStartOfDay,
  formatResponseDate,
} from "../../../../utils/common";

import { NO } from "./constants";
import {
  CoverageDecisionRecord,
  CoverageDecisionRequestPayload,
  CoverageFormValues,
  FinalDecisionFormValues,
  FinalDecisionStatus,
  FinalDecisionsWithSubmittedForm,
  isSelectableFinalDecision,
  RfpFinalDecisionResponse,
} from "./types";

// Transforms API response to include submitted form values if they exist
export function finalDecisionToFormValues(
  data: RfpFinalDecisionResponse | null,
): FinalDecisionsWithSubmittedForm | null {
  // If there are no coverage decisions, the form hasn't been submitted yet
  const hasCoverageDecisions =
    data?.coverageDecisions && data.coverageDecisions.length > 0;
  if (!hasCoverageDecisions) {
    return { submittedFormValues: null, ...data };
  }

  const currentCoverages: CoverageFormValues[] = [];
  const newCoverages: CoverageFormValues[] = [];

  // Split coverage decisions by isCurrentCoverage flag
  data.coverageDecisions.forEach((record) => {
    const formValues = coverageRecordToFormValues(record);
    if (record.isCurrentCoverage) {
      currentCoverages.push(formValues);
    } else {
      newCoverages.push(formValues);
    }
  });

  const firstCoverage = data.coverageDecisions?.length
    ? data.coverageDecisions[0]
    : null;
  const lostBOR =
    firstCoverage?.finalDecisionStatus === FinalDecisionStatus.LostBOR;
  // For the prospect wonBOR radio, we'll look at the formDraft data because
  const wonBOR = data.formData.wonBOR;

  return {
    ...data,
    submittedFormValues: {
      wonBOR,
      lostBrokerOfRecord: lostBOR || undefined,
      currentCoverages,
      newCoverages,
    },
  };
}

const toNumber = (value: string | null | undefined): number | null => {
  if (value) {
    return Number(value.trim());
  }
  return null;
};

const toString = (value: number | null | undefined): string | undefined => {
  if (typeof value === "number") {
    return String(value);
  }
  return undefined;
};

// Transforms form values to payload for backend submission
export function formValuesToCoverageRecords(
  formValues: FinalDecisionFormValues,
): {
  currentCoverages: Partial<CoverageDecisionRequestPayload>[];
  newCoverages: Partial<CoverageDecisionRequestPayload>[];
} {
  const currentCoverages =
    formValues.currentCoverages?.map((coverage) => ({
      finalDecisionStatus: deriveCoverageStatus(formValues, coverage),
      rfpProductId: coverage.rfpProductId ?? null,
      incumbentProductId: coverage.incumbentProductId ?? null,
      incumbentCarrierId: coverage.incumbentCarrierId ?? null,
      winningCarrierId: coverage.winningCarrierId ?? null,
      isCurrentCoverage: true,
      incumbentAnnualPremium: toNumber(coverage.incumbentAnnualPremium),
      incumbentCarrierGaContractStatus:
        coverage.incumbentContractedCarrier ?? null,
      winningAnnualPremium: toNumber(coverage.annualWrittenPremium),
      winningCarrierGaContractStatus: coverage.winningContractedCarrier ?? null,
      renewalDate: formatDateToStartOfDay(coverage.renewalDate ?? null),
      numberOfPlansQuoted: toNumber(coverage.numberOfPlansQuoted),
      incumbentGaOverridePercentage: toNumber(
        coverage.incumbentGaOverridePercentage,
      ),
      incumbentGaAnnualOverrideAmount: toNumber(
        coverage.incumbentGaAnnualOverrideAmount,
      ),
      rateGuarantee: toNumber(coverage.rateGuarantee),
      winningGaOverridePercentage: toNumber(
        coverage.winningGaOverridePercentage,
      ),
      winningGaAnnualOverrideAmount: toNumber(
        coverage.winningGaAnnualOverrideAmount,
      ),
      techCredits: coverage.techCredits ?? null,
      notes: coverage.notes ?? null,
    })) ?? [];

  const newCoverages =
    formValues.newCoverages?.map((coverage) => ({
      finalDecisionStatus: deriveCoverageStatus(formValues, coverage),
      rfpProductId: coverage.rfpProductId ?? null,
      winningCarrierId: coverage.winningCarrierId ?? null,
      isCurrentCoverage: false,
      winningAnnualPremium: toNumber(coverage.annualWrittenPremium),
      winningCarrierGaContractStatus: coverage.winningContractedCarrier ?? null,
      renewalDate: formatDateToStartOfDay(coverage.renewalDate ?? null),
      numberOfPlansQuoted: toNumber(coverage.numberOfPlansQuoted),
      rateGuarantee: toNumber(coverage.rateGuarantee),
      winningGaOverridePercentage: toNumber(
        coverage.winningGaOverridePercentage,
      ),
      winningGaAnnualOverrideAmount: toNumber(
        coverage.winningGaAnnualOverrideAmount,
      ),
      techCredits: coverage.techCredits ?? null,
      notes: coverage.notes ?? null,
    })) ?? [];

  return {
    currentCoverages,
    newCoverages,
  };
}

// Transform a coverage decision record from the backend into form values
export function coverageRecordToFormValues(
  record: CoverageDecisionRecord,
): CoverageFormValues {
  const getSoldValue = (): boolean | undefined => {
    if (record.finalDecisionStatus === FinalDecisionStatus.SoldNewLine) {
      return true;
    }
    if (record.finalDecisionStatus === FinalDecisionStatus.DidNotSell) {
      return false;
    }
    return false;
  };

  return {
    rfpProductId: record.rfpProductId ?? undefined,
    incumbentProductId: record.incumbentProductId ?? undefined,
    incumbentAnnualPremium: toString(record.incumbentAnnualPremium),
    incumbentContractedCarrier:
      record.incumbentCarrierGaContractStatus ?? undefined,
    incumbentGaOverridePercentage: toString(
      record.incumbentGaOverridePercentage,
    ),
    incumbentGaAnnualOverrideAmount: toString(
      record.incumbentGaAnnualOverrideAmount,
    ),
    finalDecisionSelected: isSelectableFinalDecision(record.finalDecisionStatus)
      ? record.finalDecisionStatus
      : undefined,
    winningCarrierId: record.winningCarrierId ?? undefined,
    renewalDate: formatResponseDate(record.renewalDate),
    annualWrittenPremium: toString(record.winningAnnualPremium),
    winningContractedCarrier:
      record.winningCarrierGaContractStatus ?? undefined,
    winningGaOverridePercentage: toString(record.winningGaOverridePercentage),
    winningGaAnnualOverrideAmount: toString(
      record.winningGaAnnualOverrideAmount,
    ),
    rateGuarantee: toString(record.rateGuarantee),
    techCredits: record.techCredits ?? undefined,
    notes: record.notes ?? undefined,
    numberOfPlansQuoted: toString(record.numberOfPlansQuoted),
    sold: getSoldValue(),
  };
}

// Derives the finalDecisionStatus for the API given form values
export function deriveCoverageStatus(
  formValues: FinalDecisionFormValues,
  coverageFormData: CoverageFormValues,
): FinalDecisionStatus | null {
  // BOR Not Won if user selected No radio
  if (formValues.wonBOR === NO) {
    return FinalDecisionStatus.BORNotWon;
  }

  // Lost BOR if checkbox is checked
  if (formValues.lostBrokerOfRecord) {
    return FinalDecisionStatus.LostBOR;
  }

  // Check a selected status from Final Decisions radio group
  if (coverageFormData.finalDecisionSelected) {
    return coverageFormData.finalDecisionSelected;
  }

  // Did Not Sell if sold is explicitly false
  if (coverageFormData.sold === false) {
    return FinalDecisionStatus.DidNotSell;
  }

  // Sold New Line if sold checkbox is checked
  if (coverageFormData.sold === true) {
    return FinalDecisionStatus.SoldNewLine;
  }

  // The default value is Did Not Sell
  // We need to do this in the scenario where the user did not select anything
  // specifically for new coverages, if they keep the Sold checkbox unchecked
  return FinalDecisionStatus.DidNotSell;
}
